import React, { useState, useCallback, useEffect, useMemo } from 'react'
import SlideBase from '../SlideBase'
import WrappedImageBackground from '../../WrappedImageBackground'
import WorldMapAnimation from './WorldMapAnimation'
import QuizContent from './QuizContent'
import { buildHotelUrl } from 'home/components/TrendingDestinations/utils'
import FadeBackground from '../../FadeBackground'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import { AVERAGE_TOP_DESTINATIONS } from '../../../config/averages'

const SLIDE_ID = 'top-destinations-quiz' as const

function TopDestinationsQuizSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isActiveSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const dispatch = useWrappedStoryDispatch()

  const [phase, setPhase] = useState<'question' | 'answer'>('question')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { topDestinations: userTopDestinations, stats } = data || {}

  // Use average data when user has no searched destinations
  const isAverageMode = !userTopDestinations?.length
  const topDestinations = useMemo(
    () => isAverageMode ? AVERAGE_TOP_DESTINATIONS : userTopDestinations,
    [isAverageMode, userTopDestinations],
  )
  const correctAnswer = topDestinations?.[0]

  // Set context phase to 'question' when this slide becomes active to stop the timer
  useEffect(() => {
    if (isActiveSlide && phase === 'question') {
      dispatch({ type: WrappedStoryActions.setPhase, phase: 'question' })
    }
  }, [dispatch, isActiveSlide, phase])

  const handleSelect = useCallback((destinationId: string) => {
    if (phase === 'answer') {
      const url = buildHotelUrl(topDestinations?.find(topDestination => topDestination.id === destinationId)?.name ?? '', destinationId)
      window.open(url, '_blank')
    } else {
      setSelectedId(destinationId)
      setPhase('answer')
      // Reset context phase to undefined to resume the timer
      dispatch({ type: WrappedStoryActions.setPhase, phase: undefined })
    }
  }, [phase, topDestinations, dispatch])

  if (!stats || !correctAnswer) return null

  if (phase === 'question') {
    return (
      <SlideBase verticalAlign="space-between">
        <WorldMapAnimation />
        <QuizContent
          topDestinations={topDestinations}
          stats={stats}
          phase={phase}
          correctAnswerId={correctAnswer.id}
          selectedId={selectedId}
          onSelect={handleSelect}
          isAverageMode={isAverageMode}
        />
      </SlideBase>
    )
  }

  return (
    <SlideBase
      verticalAlign={!correctAnswer?.imageId ? 'space-between' : 'end'}
      background={
        correctAnswer?.imageId && (
          <WrappedImageBackground
            gradientOverlay={false}
            imageId={correctAnswer.imageId}
            alt={correctAnswer.name}
            kenBurnsVariant="zoom"
            isActive={isActiveSlide && !isPaused}
          />
        )
      }
    >
      {!correctAnswer?.imageId && <WorldMapAnimation />}
      <FadeBackground variant="solid">
        <QuizContent
          topDestinations={topDestinations}
          stats={stats}
          phase={phase}
          correctAnswerId={correctAnswer.id}
          selectedId={selectedId}
          onSelect={handleSelect}
          isAverageMode={isAverageMode}
        />
      </FadeBackground>
    </SlideBase>
  )
}

export default React.memo(TopDestinationsQuizSlide)
